import React, { useState } from 'react'
import {X, AlertTriangle, Bug, User} from 'lucide-react'

interface ReportModalProps {
  isOpen: boolean
  onClose: () => void
}

export function ReportModal({ isOpen, onClose }: ReportModalProps) {
  const [reportType, setReportType] = useState<'bug' | 'user'>('bug')
  const [reportedBy, setReportedBy] = useState('')
  const [reporterEmail, setReporterEmail] = useState('')
  const [reportedUser, setReportedUser] = useState('')
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    try {
      const response = await fetch('https://api.lumi.new/v1/functions/p411756259739451392/submitReport', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reportType,
          reportedBy: reportedBy || 'Anonymous',
          reporterEmail,
          reportedUser: reportType === 'user' ? reportedUser : '',
          subject,
          description
        })
      })

      if (response.ok) {
        setSubmitted(true)
        setTimeout(() => {
          onClose()
          // Reset form
          setReportType('bug')
          setReportedBy('')
          setReporterEmail('')
          setReportedUser('')
          setSubject('')
          setDescription('')
          setSubmitted(false)
        }, 2000)
      }
    } catch (error) {
      console.error('Error submitting report:', error)
      alert('Failed to submit report. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-black border border-deep-purple-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-black border-b border-deep-purple-800 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://static.lumi.new/2d/2db01c8af5f8d23adc3535b856a32a22.webp" 
              alt="XyberMart" 
              className="h-8 object-contain"
            />
            <h2 className="text-2xl font-bold text-white">Report Bug / User</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {submitted ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-electric-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-electric-purple-500" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Report Submitted</h3>
              <p className="text-gray-400">Thank you for your feedback. We'll review it shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">Report Type</label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setReportType('bug')}
                    className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-all ${
                      reportType === 'bug'
                        ? 'border-electric-purple-500 bg-electric-purple-500/10'
                        : 'border-deep-purple-800 hover:border-deep-purple-600'
                    }`}
                  >
                    <Bug className="w-5 h-5" />
                    <span className="font-medium text-white">Bug Report</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setReportType('user')}
                    className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-all ${
                      reportType === 'user'
                        ? 'border-electric-purple-500 bg-electric-purple-500/10'
                        : 'border-deep-purple-800 hover:border-deep-purple-600'
                    }`}
                  >
                    <User className="w-5 h-5" />
                    <span className="font-medium text-white">Report User</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Your Name / Agent Name <span className="text-gray-500">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={reportedBy}
                  onChange={(e) => setReportedBy(e.target.value)}
                  placeholder="Anonymous"
                  className="w-full bg-deep-purple-950/30 border border-deep-purple-800 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email <span className="text-gray-500">(Optional, for follow-up)</span>
                </label>
                <input
                  type="email"
                  value={reporterEmail}
                  onChange={(e) => setReporterEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full bg-deep-purple-950/30 border border-deep-purple-800 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
                />
              </div>

              {reportType === 'user' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    User Being Reported *
                  </label>
                  <input
                    type="text"
                    value={reportedUser}
                    onChange={(e) => setReportedUser(e.target.value)}
                    placeholder="Agent name or ID"
                    required={reportType === 'user'}
                    className="w-full bg-deep-purple-950/30 border border-deep-purple-800 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={reportType === 'bug' ? 'Brief description of the bug' : 'Why are you reporting this user?'}
                  required
                  className="w-full bg-deep-purple-950/30 border border-deep-purple-800 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description *
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={
                    reportType === 'bug'
                      ? 'Detailed description of the bug, steps to reproduce, expected vs actual behavior...'
                      : 'Provide details about why this user should be reviewed or removed...'
                  }
                  required
                  rows={6}
                  className="w-full bg-deep-purple-950/30 border border-deep-purple-800 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500 resize-none"
                />
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-3 bg-deep-purple-950/50 border border-deep-purple-800 text-white rounded-lg hover:bg-deep-purple-950/70 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-electric-purple-500 to-deep-purple-600 text-white rounded-lg hover:from-electric-purple-600 hover:to-deep-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
